import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { formatDate, formatDateTime, apiErrorMsg } from '@/lib/formatters';
import {
  CalendarDays, Plus, ChevronLeft, ChevronRight, MapPin, Clock, User,
  CheckCircle, XCircle, Eye, Building2, Users as UsersIcon, AlertTriangle,
  CalendarCheck, CalendarX, RotateCcw, Phone
} from 'lucide-react';

const VISIT_STATUS = {
  scheduled: { label: 'Scheduled', color: 'bg-blue-100 text-blue-800 border-blue-200', icon: Clock },
  confirmed: { label: 'Confirmed', color: 'bg-indigo-100 text-indigo-800 border-indigo-200', icon: CalendarCheck },
  completed: { label: 'Completed', color: 'bg-emerald-100 text-emerald-800 border-emerald-200', icon: CheckCircle },
  rescheduled: { label: 'Rescheduled', color: 'bg-amber-100 text-amber-800 border-amber-200', icon: RotateCcw },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-700 border-red-200', icon: CalendarX },
  no_show: { label: 'No Show', color: 'bg-slate-200 text-slate-700 border-slate-300', icon: XCircle },
};

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

export default function SiteVisitsPage() {
  const { user } = useAuth();
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('calendar');
  const [showCreate, setShowCreate] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [projectFilter, setProjectFilter] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [projects, setProjects] = useState([]);
  const [executives, setExecutives] = useState([]);

  const fetchVisits = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (projectFilter) params.project_id = projectFilter;
      const { data } = await api.get('/visits', { params });
      setVisits(data.visits);
    } catch { toast.error('Failed to load visits'); }
    finally { setLoading(false); }
  }, [statusFilter, projectFilter]);

  useEffect(() => { fetchVisits(); }, [fetchVisits]);
  useEffect(() => {
    api.get('/projects').then(r => setProjects(r.data.projects)).catch(() => {});
    api.get('/users/executives').then(r => setExecutives(r.data.executives)).catch(() => {});
  }, []);

  const stats = useMemo(() => ({
    total: visits.length,
    scheduled: visits.filter(v => v.status === 'scheduled' || v.status === 'confirmed').length,
    completed: visits.filter(v => v.status === 'completed').length,
    cancelled: visits.filter(v => v.status === 'cancelled' || v.status === 'no_show').length,
    today: visits.filter(v => v.date === new Date().toISOString().split('T')[0]).length,
  }), [visits]);

  const visitsByDate = useMemo(() => {
    const map = {};
    visits.forEach(v => {
      if (!map[v.date]) map[v.date] = [];
      map[v.date].push(v);
    });
    return map;
  }, [visits]);

  const dayVisits = useMemo(() => visitsByDate[selectedDate] || [], [visitsByDate, selectedDate]);

  return (
    <div className="space-y-5" data-testid="site-visits-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Site Visits</h1>
          <p className="text-slate-500 text-sm mt-0.5">{stats.total} visits &middot; {stats.scheduled} upcoming &middot; {stats.completed} completed</p>
        </div>
        {user?.role !== 'finance' && (
          <Button onClick={() => setShowCreate(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="schedule-visit-btn">
            <Plus size={16} /> Schedule Visit
          </Button>
        )}
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: 'Total Visits', value: stats.total, icon: CalendarDays, color: 'text-blue-600', bg: 'bg-blue-50' },
          { label: 'Today', value: stats.today, icon: Clock, color: 'text-purple-600', bg: 'bg-purple-50' },
          { label: 'Scheduled', value: stats.scheduled, icon: CalendarCheck, color: 'text-indigo-600', bg: 'bg-indigo-50' },
          { label: 'Completed', value: stats.completed, icon: CheckCircle, color: 'text-emerald-600', bg: 'bg-emerald-50' },
          { label: 'Cancelled / No-show', value: stats.cancelled, icon: CalendarX, color: 'text-red-600', bg: 'bg-red-50' },
        ].map(kpi => (
          <Card key={kpi.label} className="border-slate-200 kpi-card">
            <CardContent className="p-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold">{kpi.label}</p>
                  <p className="text-xl font-extrabold font-mono mt-0.5">{kpi.value}</p>
                </div>
                <div className={`w-9 h-9 rounded-lg ${kpi.bg} flex items-center justify-center`}>
                  <kpi.icon size={18} className={kpi.color} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <Select value={statusFilter} onValueChange={v => setStatusFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-[150px] h-9 text-sm" data-testid="visit-status-filter"><SelectValue placeholder="All Statuses" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {Object.entries(VISIT_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={projectFilter} onValueChange={v => setProjectFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-[180px] h-9 text-sm" data-testid="visit-project-filter"><SelectValue placeholder="All Projects" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Projects</SelectItem>
            {projects.map(p => <SelectItem key={p._id} value={p._id}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Tabs value={view} onValueChange={setView}>
        <TabsList>
          <TabsTrigger value="calendar" data-testid="visits-calendar-tab"><CalendarDays size={14} className="mr-1" /> Calendar</TabsTrigger>
          <TabsTrigger value="list" data-testid="visits-list-tab"><Clock size={14} className="mr-1" /> List View</TabsTrigger>
        </TabsList>

        <TabsContent value="calendar">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Calendar Grid */}
            <div className="lg:col-span-2">
              <CalendarGrid
                currentMonth={currentMonth}
                setCurrentMonth={setCurrentMonth}
                visitsByDate={visitsByDate}
                selectedDate={selectedDate}
                onSelectDate={setSelectedDate}
              />
            </div>
            {/* Day Detail Panel */}
            <div>
              <Card className="border-slate-200 sticky top-20">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold flex items-center justify-between">
                    <span>{formatDate(selectedDate)}</span>
                    <Badge variant="outline" className="font-mono text-xs">{dayVisits.length} visits</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 max-h-[500px] overflow-y-auto">
                  {dayVisits.length === 0 ? (
                    <p className="text-sm text-slate-400 text-center py-6">No visits on this date</p>
                  ) : (
                    dayVisits.map(visit => (
                      <VisitCard key={visit._id} visit={visit} onClick={() => setShowDetail(visit)} />
                    ))
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="list">
          <div className="table-container bg-white">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-xs uppercase tracking-wider font-semibold">Date & Time</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold">Lead / Customer</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold">Project</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold">Executive</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold">Status</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold">Visitors</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold">Outcome</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? Array.from({ length: 6 }).map((_, i) => (
                  <TableRow key={i}>{Array.from({ length: 8 }).map((_, j) => <TableCell key={j}><div className="h-4 bg-slate-100 rounded animate-pulse" /></TableCell>)}</TableRow>
                )) : visits.length === 0 ? (
                  <TableRow><TableCell colSpan={8} className="text-center py-12 text-slate-400">
                    <CalendarDays size={32} className="mx-auto mb-2 text-slate-300" />No site visits found
                  </TableCell></TableRow>
                ) : visits.map(v => {
                  const st = VISIT_STATUS[v.status] || VISIT_STATUS.scheduled;
                  return (
                    <TableRow key={v._id} data-testid={`visit-row-${v._id}`} className="cursor-pointer hover:bg-slate-50/80" onClick={() => setShowDetail(v)}>
                      <TableCell>
                        <div className="text-sm font-medium">{formatDate(v.date)}</div>
                        <div className="text-xs text-slate-400">{v.time || '-'}</div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm font-semibold">{v.lead_name}</div>
                        <div className="text-xs text-slate-400">{v.lead_id}</div>
                      </TableCell>
                      <TableCell className="text-sm">{v.project_name || '-'}</TableCell>
                      <TableCell className="text-sm text-slate-600">{v.executive_name || '-'}</TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${st.color}`}>
                          <st.icon size={12} /> {st.label}
                        </span>
                      </TableCell>
                      <TableCell className="text-sm text-center">{v.visitors || '-'}</TableCell>
                      <TableCell className="text-xs text-slate-500 max-w-[120px] truncate">{v.outcome || '-'}</TableCell>
                      <TableCell>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={e => { e.stopPropagation(); setShowDetail(v); }}>
                          <Eye size={14} />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      {showCreate && (
        <CreateVisitDialog
          open onClose={() => setShowCreate(false)}
          projects={projects} executives={executives} user={user}
          onCreated={() => { fetchVisits(); setShowCreate(false); }}
        />
      )}
      {showDetail && (
        <VisitDetailDialog
          visit={showDetail} onClose={() => setShowDetail(null)}
          user={user} onUpdated={() => { fetchVisits(); setShowDetail(null); }}
        />
      )}
    </div>
  );
}

/* ─── Calendar Grid ─── */
function CalendarGrid({ currentMonth, setCurrentMonth, visitsByDate, selectedDate, onSelectDate }) {
  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const today = new Date().toISOString().split('T')[0];

  const prevMonth = () => setCurrentMonth(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentMonth(new Date(year, month + 1, 1));

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <Card className="border-slate-200">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={prevMonth} data-testid="cal-prev-month">
            <ChevronLeft size={18} />
          </Button>
          <h3 className="text-base font-semibold text-slate-800">{MONTHS[month]} {year}</h3>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={nextMonth} data-testid="cal-next-month">
            <ChevronRight size={18} />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="px-3 pb-3">
        <div className="grid grid-cols-7 gap-px">
          {DAYS.map(d => (
            <div key={d} className="text-center text-[10px] font-semibold text-slate-400 uppercase tracking-wider py-2">{d}</div>
          ))}
          {cells.map((day, i) => {
            if (day === null) return <div key={`e-${i}`} />;
            const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const dayVisits = visitsByDate[dateStr] || [];
            const isToday = dateStr === today;
            const isSelected = dateStr === selectedDate;
            const hasVisits = dayVisits.length > 0;

            return (
              <button
                key={dateStr}
                onClick={() => onSelectDate(dateStr)}
                data-testid={`cal-day-${dateStr}`}
                className={`
                  relative p-1.5 min-h-[52px] rounded-lg text-left transition-all text-sm
                  ${isSelected ? 'bg-emerald-50 ring-2 ring-emerald-500' : 'hover:bg-slate-50'}
                  ${isToday && !isSelected ? 'bg-blue-50' : ''}
                `}
              >
                <span className={`text-xs font-medium ${isToday ? 'text-blue-600 font-bold' : 'text-slate-700'}`}>{day}</span>
                {hasVisits && (
                  <div className="mt-0.5 flex flex-wrap gap-0.5">
                    {dayVisits.slice(0, 3).map((v, vi) => {
                      const statusColor = v.status === 'completed' ? 'bg-emerald-500' : v.status === 'cancelled' || v.status === 'no_show' ? 'bg-red-400' : 'bg-blue-500';
                      return <div key={vi} className={`w-1.5 h-1.5 rounded-full ${statusColor}`} title={v.lead_name} />;
                    })}
                    {dayVisits.length > 3 && <span className="text-[9px] text-slate-400">+{dayVisits.length - 3}</span>}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

/* ─── Visit Card (for day panel) ─── */
function VisitCard({ visit, onClick }) {
  const st = VISIT_STATUS[visit.status] || VISIT_STATUS.scheduled;
  return (
    <div
      className="p-3 rounded-lg border border-slate-200 hover:border-emerald-300 hover:shadow-sm cursor-pointer transition-all"
      onClick={onClick}
      data-testid={`visit-card-${visit._id}`}
    >
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-sm font-semibold text-slate-800">{visit.lead_name}</span>
        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium border ${st.color}`}>
          <st.icon size={10} /> {st.label}
        </span>
      </div>
      <div className="space-y-0.5 text-xs text-slate-500">
        {visit.time && <div className="flex items-center gap-1"><Clock size={11} /> {visit.time}</div>}
        {visit.project_name && <div className="flex items-center gap-1"><Building2 size={11} /> {visit.project_name}</div>}
        {visit.executive_name && <div className="flex items-center gap-1"><User size={11} /> {visit.executive_name}</div>}
        {visit.visitors > 0 && <div className="flex items-center gap-1"><UsersIcon size={11} /> {visit.visitors} visitor{visit.visitors > 1 ? 's' : ''}</div>}
      </div>
    </div>
  );
}

/* ─── Create Visit Dialog ─── */
function CreateVisitDialog({ open, onClose, projects, executives, user, onCreated }) {
  const [form, setForm] = useState({
    lead_id: '', lead_name: '', project_id: '', project_name: '',
    executive_id: user?._id || user?.id || '', executive_name: user?.name || '',
    date: new Date().toISOString().split('T')[0], time: '10:00',
    visitors: 1, remarks: '',
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.lead_name || !form.date) return toast.error('Lead name and date required');
    setSaving(true);
    try {
      const proj = projects.find(p => p._id === form.project_id);
      const exec = executives.find(ex => ex._id === form.executive_id);
      await api.post('/visits', {
        ...form,
        project_name: proj?.name || form.project_name,
        executive_name: exec?.name || form.executive_name,
        visitors: Number(form.visitors) || 1,
      });
      toast.success('Site visit scheduled');
      onCreated();
    } catch (err) { toast.error(apiErrorMsg(err)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Schedule Site Visit</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Lead ID</Label><Input value={form.lead_id} onChange={e => setForm({ ...form, lead_id: e.target.value })} placeholder="VIR-0001" data-testid="visit-lead-id" /></div>
            <div><Label>Customer Name *</Label><Input value={form.lead_name} onChange={e => setForm({ ...form, lead_name: e.target.value })} required data-testid="visit-lead-name" /></div>
            <div>
              <Label>Project</Label>
              <Select value={form.project_id} onValueChange={v => setForm({ ...form, project_id: v })}>
                <SelectTrigger data-testid="visit-project"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{projects.map(p => <SelectItem key={p._id} value={p._id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Executive</Label>
              <Select value={form.executive_id} onValueChange={v => setForm({ ...form, executive_id: v })}>
                <SelectTrigger data-testid="visit-executive"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{executives.map(ex => <SelectItem key={ex._id} value={ex._id}>{ex.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Date *</Label><Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} required data-testid="visit-date" /></div>
            <div><Label>Time</Label><Input type="time" value={form.time} onChange={e => setForm({ ...form, time: e.target.value })} data-testid="visit-time" /></div>
            <div><Label>Visitors</Label><Input type="number" min={1} value={form.visitors} onChange={e => setForm({ ...form, visitors: e.target.value })} /></div>
          </div>
          <div><Label>Remarks</Label><Textarea value={form.remarks} onChange={e => setForm({ ...form, remarks: e.target.value })} rows={2} /></div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="submit-visit-btn">{saving ? 'Scheduling...' : 'Schedule Visit'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* ─── Visit Detail / Update Dialog ─── */
function VisitDetailDialog({ visit, onClose, user, onUpdated }) {
  const [updating, setUpdating] = useState(false);

  const updateStatus = async (newStatus) => {
    setUpdating(true);
    try {
      await api.put(`/visits/${visit._id}`, { status: newStatus, lead_id: visit.lead_id });
      toast.success(`Visit marked as ${newStatus}`);
      onUpdated();
    } catch (err) { toast.error(apiErrorMsg(err)); }
    finally { setUpdating(false); }
  };

  const st = VISIT_STATUS[visit.status] || VISIT_STATUS.scheduled;

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Visit: {visit.lead_name}
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${st.color}`}>
              <st.icon size={12} /> {st.label}
            </span>
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
          <div><span className="text-slate-400">Lead ID:</span> <span className="font-mono font-medium">{visit.lead_id || '-'}</span></div>
          <div><span className="text-slate-400">Date:</span> <span className="font-medium">{formatDate(visit.date)}</span></div>
          <div><span className="text-slate-400">Time:</span> <span className="font-medium">{visit.time || '-'}</span></div>
          <div><span className="text-slate-400">Project:</span> <span className="font-medium">{visit.project_name || '-'}</span></div>
          <div><span className="text-slate-400">Executive:</span> <span className="font-medium">{visit.executive_name || '-'}</span></div>
          <div><span className="text-slate-400">Visitors:</span> <span className="font-medium">{visit.visitors || '-'}</span></div>
          {visit.outcome && <div className="col-span-2"><span className="text-slate-400">Outcome:</span> <span className="font-medium">{visit.outcome}</span></div>}
          {visit.feedback && <div className="col-span-2"><span className="text-slate-400">Feedback:</span> <span className="font-medium">{visit.feedback}</span></div>}
          {visit.remarks && <div className="col-span-2"><span className="text-slate-400">Remarks:</span> <span className="font-medium">{visit.remarks}</span></div>}
          {visit.next_action && <div className="col-span-2"><span className="text-slate-400">Next Action:</span> <span className="font-medium">{visit.next_action}</span></div>}
        </div>

        {/* Status Actions */}
        {user?.role !== 'finance' && visit.status !== 'completed' && visit.status !== 'cancelled' && (
          <div className="flex flex-wrap gap-2 pt-4 border-t border-slate-100 mt-4">
            <p className="w-full text-xs text-slate-400 font-semibold uppercase tracking-wider mb-1">Update Status</p>
            {visit.status !== 'confirmed' && (
              <Button size="sm" variant="outline" className="text-indigo-600 border-indigo-200 hover:bg-indigo-50" onClick={() => updateStatus('confirmed')} disabled={updating} data-testid="visit-confirm-btn">
                <CalendarCheck size={14} /> Confirm
              </Button>
            )}
            <Button size="sm" variant="outline" className="text-emerald-600 border-emerald-200 hover:bg-emerald-50" onClick={() => updateStatus('completed')} disabled={updating} data-testid="visit-complete-btn">
              <CheckCircle size={14} /> Mark Completed
            </Button>
            <Button size="sm" variant="outline" className="text-amber-600 border-amber-200 hover:bg-amber-50" onClick={() => updateStatus('rescheduled')} disabled={updating} data-testid="visit-reschedule-btn">
              <RotateCcw size={14} /> Reschedule
            </Button>
            <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => updateStatus('cancelled')} disabled={updating} data-testid="visit-cancel-btn">
              <CalendarX size={14} /> Cancel
            </Button>
            <Button size="sm" variant="outline" className="text-slate-600 border-slate-200 hover:bg-slate-50" onClick={() => updateStatus('no_show')} disabled={updating} data-testid="visit-noshow-btn">
              <XCircle size={14} /> No Show
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
